import { createContext, useMemo, useRef, useState } from 'react';

import { initForage } from './utils';

const ForageContext = createContext(null);

/**
 * Provider component that manages Forage payment processing state and provides
 * Forage-related functionality to child components.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with Forage context
 * @returns {JSX.Element} ForageContext.Provider with context value
 */
function ForageProvider({ children }) {
    const [forage, setForage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const customerIDRef = useRef(null);

    /**
     * Initializes a new Forage instance with the provided customer ID.
     *
     * @param {string} customerID - The customer ID to initialize Forage with
     * @returns {Promise<Object>} The initialized Forage instance
     * @throws {Error} When customerID is not provided or initialization fails
     */
    const initializeForage = async (customerID) => {
        if (!customerID) {
            throw new Error('Customer ID is required');
        }

        setIsLoading(true);
        setError(null);

        try {
            const forageInstance = await initForage({ customerID });
            setForage(forageInstance);
            customerIDRef.current = customerID;
            return forageInstance;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Refreshes the current Forage session with existing customerID.
     *
     * @returns {Promise<Object>} The new Forage instance
     * @throws {Error} When Forage is not initialized or refresh fails
     */
    const refreshSession = async () => {
        if (!forage) {
            throw new Error('Forage not initialized');
        }

        setForage(null);
        setIsLoading(true);
        setError(null);

        try {
            const newForageInstance = await initForage({
                customerID: customerIDRef.current,
            });
            setForage(newForageInstance);
            return newForageInstance;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Clears the current Forage instance, resetting the context to its initial state.
     */
    const clearForage = () => {
        setForage(null);
    };

    const contextValue = useMemo(
        () => ({
            forage,
            isLoading,
            error,
            isReady: !!forage,
            initializeForage,
            refreshSession,
            clearForage,
        }),
        [forage, isLoading, error, initializeForage, refreshSession, clearForage]
    );

    return <ForageContext.Provider value={contextValue}>{children}</ForageContext.Provider>;
}

export { ForageContext, ForageProvider };
